import { DefaultTheme, NavigationContainer } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import React, { useContext, useEffect } from 'react';

// context
import { AuthContext } from '@streamster/shared/screens/auth/auth.utils';

// screens
import { AdminBillingScreen } from '#/screens/adminBilling/AdminBilling.screen';
import { AdminProfileScreen } from '#/screens/adminProfile/adminProfile.screen';
import { AuthCallbackScreen } from '#/screens/authCallback/AuthCallback.screen';
import { AuthOverrideScreen } from '#/screens/authOverride/AuthOverride.screen';
import { BillingScreen } from '#/screens/billing/Billing.screen';
import { ChatsScreen } from '#/screens/chats/Chats.screen';
import { OverlaysScreen } from '#/screens/overlays/Overlays.screen';
import { ReferAFriendScreen } from '#/screens/referAFriend/ReferAFriend.screen';
import { StatisticsScreen } from '#/screens/statistics/Statistics.screen';
import { TariffScreen } from '#/screens/tariff/Tariff.screen';
import { WebNavigation } from '../components/WebNavigation/WebNavigation';
import ActivateScreen from '../screens/activate/activate.screen';
import ActiveStreamScreen from '../screens/activeStream/ActiveStream.screen';
import AuthScreen from '../screens/auth/auth.screen';
import ConnectScreen from '@streamster/shared/screens/connect/connect.screen';
import HomeScreen from '../screens/home/home.screen.web';
import { ProfileScreen } from '../screens/profile/Profile.screen';
import RegisterScreen from '../screens/register/register.screen';
import ResetPasswordScreen from '../screens/resetPassword/resetPassword.screen';
import ThanksForSubscribingScreen from '../screens/thanksForSubscrbing';
import LogRouteToGoogleAnalytics from './logRouteToGoogleAnalytics';
import { navigationRef } from '@streamster/shared/navigation/navigation.ref';
import type { StackParamList } from '@streamster/shared/navigation/navigation.types';
import { AdminCouponCodesScreen } from '#/screens/adminCouponCodes/AdminCouponCodes.screen';

const Stack = createStackNavigator<StackParamList>();

const config = {
  screens: {
    Connect: 'connect',
    SignIn: 'login',
    Register: 'register/:referralCode?',
    RegisterCoupon: 'register-coupon/:referralCode?',
    Home: 'home',
    Profile: 'profile',
    Tariff: 'tariff',
    Statistics: 'statistics',
    ActiveStream: 'stream',
    ResetPassword: 'reset-password',
    ThanksForSubscribing: 'thanks-for-subscribing',
    ReferAFriend: 'refer-a-friend',
    Activate: 'activate',
    Overlays: 'overlays',
    Chats: 'chats',
    AuthOverrideScreen: 'auth-override',
    AdminProfile: 'admin-profile',
    AdminBilling: 'admin-billing',
    AuthCallback: 'auth-callback',
    Billing: 'billing',
    // DEPRECATED — 'Support' (Freshdesk) is no longer mapped or registered, so
    // /support does not resolve to a screen any more. Pending teardown; see
    // streamster-account/docs/freshdesk-removal.md.
    AdminCouponCodes: 'coupon-codes',
  },
};

const linking = {
  prefixes: [],
  config,
};

const MyTheme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    background: 'transparent',
  },
};

export default function Navigation() {
  const [isInited, setIsInited] = React.useState(false);
  const { isAuth } = useContext(AuthContext);

  useEffect(() => setIsInited(true), []);

  return (
    <NavigationContainer linking={linking} theme={MyTheme} ref={navigationRef}>
      {isAuth && isInited && <WebNavigation />}
      <LogRouteToGoogleAnalytics />
      <Stack.Navigator
        initialRouteName={isAuth ? 'Connect' : 'SignIn'}
        screenOptions={{ headerShown: false, detachPreviousScreen: true }}
      >
        {isAuth ? (
          <>
            <Stack.Screen name="Connect" component={ConnectScreen} />
            <Stack.Screen name="Home" component={HomeScreen} />
            <Stack.Screen name="Profile" component={ProfileScreen} />
            <Stack.Screen name="Tariff" component={TariffScreen} />
            <Stack.Screen name="Statistics" component={StatisticsScreen} />
            <Stack.Screen name="Overlays" component={OverlaysScreen} />
            <Stack.Screen name="Chats" component={ChatsScreen} />
            <Stack.Screen name="Billing" component={BillingScreen} />
            <Stack.Screen name="AuthOverride" component={AuthOverrideScreen} />
            <Stack.Screen name="AdminProfile" component={AdminProfileScreen} />
            <Stack.Screen name="AdminBilling" component={AdminBillingScreen} />
            <Stack.Screen name="AdminCouponCodes" component={AdminCouponCodesScreen} />
            <Stack.Screen
              name="ReferAFriend"
              component={ReferAFriendScreen}
              options={{ title: 'Refer a friend' }}
            />
            <Stack.Screen
              name="ActiveStream"
              component={ActiveStreamScreen}
              options={{ title: 'Live stream' }}
            />
            <Stack.Screen
              name="ThanksForSubscribing"
              component={ThanksForSubscribingScreen}
              options={{ title: 'Thanks for subscribing' }}
            />
            <Stack.Screen
              name="Activate"
              component={ActivateScreen}
              options={{ title: 'Activate your account' }}
            />
            <Stack.Screen
              name="AuthCallback"
              component={AuthCallbackScreen}
              options={{ title: 'Logging in...' }}
            />
          </>
        ) : (
          <>
            <Stack.Screen name="SignIn" component={AuthScreen} options={{ title: 'Sign in' }} />
            <Stack.Screen
              name="RegisterCoupon"
              options={{ title: 'Register' }}
              component={RegisterScreen}
            />
            <Stack.Screen name="Register" component={RegisterScreen} />
            <Stack.Screen
              name="ResetPassword"
              component={ResetPasswordScreen}
              options={{ title: 'Reset password' }}
            />
            <Stack.Screen
              name="Activate"
              component={ActivateScreen}
              options={{ title: 'Activate your account' }}
            />
            <Stack.Screen
              name="ThanksForSubscribing"
              component={ThanksForSubscribingScreen}
              options={{ title: 'Thanks for subscribing' }}
            />
            <Stack.Screen
              name="AuthCallback"
              component={AuthCallbackScreen}
              options={{ title: 'Logging in...' }}
            />
          </>
        )}
      </Stack.Navigator>
    </NavigationContainer>
  );
}
